import React, { useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { palette } from '../../theme/colors';

interface ResellerProfile {
  id: string;
  companyName: string;
  defaultTier?: {
    tierName: string;
  };
  user: {
    email: string;
    firstName: string;
    lastName: string;
  };
}

interface PortalContext {
  portalInfo: {
    organization: { name: string };
  };
  isAuthenticated: boolean;
  user: { email: string; firstName?: string; lastName?: string } | null;
}

const fetchMyProfile = async (orgSlug: string, token: string): Promise<ResellerProfile> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/b2b-portal/${orgSlug}/my-profile`,
    {
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error('Failed to fetch profile');
  }
  return response.json();
};

export const B2BAccountPage: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const { portalInfo, isAuthenticated, user } = useOutletContext<PortalContext>();
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/b2b/${orgSlug}/login`);
    }
  }, [isAuthenticated, navigate, orgSlug]);

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['b2b-my-profile', orgSlug],
    queryFn: () => fetchMyProfile(orgSlug!, token!),
    enabled: !!orgSlug && !!token,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load profile. Please try again.
      </Alert>
    );
  }

  const quickLinks = [
    {
      title: 'Browse Products',
      description: 'View our B2B catalog',
      icon: <InventoryIcon sx={{ fontSize: 40 }} />,
      path: `/b2b/${orgSlug}/products`,
    },
    {
      title: 'My Quotes',
      description: 'View quote requests',
      icon: <RequestQuoteIcon sx={{ fontSize: 40 }} />,
      path: `/b2b/${orgSlug}/quotes`,
    },
    {
      title: 'My Reservations',
      description: 'View your reservations',
      icon: <ShoppingBasketIcon sx={{ fontSize: 40 }} />,
      path: `/b2b/${orgSlug}/reservations`,
    },
    {
      title: 'My Invoices',
      description: 'View invoices & payments',
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      path: `/b2b/${orgSlug}/invoices`,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        <AccountCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        My Account
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome back to {portalInfo.organization.name} B2B Portal
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: palette.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <AccountCircleIcon sx={{ fontSize: 48, color: '#fff' }} />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  {profile?.user?.firstName} {profile?.user?.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BusinessIcon sx={{ color: palette.textMuted }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {profile?.companyName || 'N/A'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalOfferIcon sx={{ color: palette.textMuted }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Pricing Tier
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                    {profile?.defaultTier?.tierName || 'Standard'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Links */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Quick Links
          </Typography>
          <Grid container spacing={2}>
            {quickLinks.map((link) => (
              <Grid item xs={12} sm={6} md={3} key={link.title}>
                <Card
                  component={RouterLink}
                  to={link.path}
                  sx={{
                    textDecoration: 'none',
                    display: 'block',
                    height: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Box sx={{ color: palette.primary, mb: 2 }}>{link.icon}</Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {link.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {link.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
